import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  signal,
  computed,
} from "@angular/core";
import { CommonModule, DecimalPipe } from "@angular/common";
import { GroupedPayment } from "../../interfaces/payment.interface";
import OrderPaymentAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/process/order-payment/order-payment.entity";
import { SmartGridComponent } from 'app/shared/components/smart-grid/components/smart-grid/smart-grid.component';
import { ColDef } from 'app/shared/components/smart-grid/core/interfaces/col-def.interface';
import { GridConfig } from 'app/shared/components/smart-grid/core/interfaces/grid-config.interface';
import { PaymentMethodActionsCellRendererComponent } from './cell-renderers/payment-method-actions-cell-renderer.component';
import { DecimalCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/decimal-cell-renderer/decimal-cell-renderer.component';
import { PaymentFooterCellRendererComponent } from './cell-renderers/payment-footer-cell-renderer.component';

@Component({
  selector: "app-payment-methods-tab",
  standalone: true,
  imports: [
    CommonModule,
    SmartGridComponent,
  ],
  templateUrl: "./payment-methods-tab.component.html",
  styleUrls: ["./payment-methods-tab.component.scss"],
})
export class PaymentMethodsTabComponent implements OnInit, OnChanges {
  @Input() payments: GroupedPayment[] = [];
  @Input() rawPayments: OrderPaymentAttributes[] = [];
  @Input() totalUnit: string = "";
  @Input() showActions: boolean = true;

  @Output() onOpenCreditNote = new EventEmitter<GroupedPayment>();
  @Output() onToggleInner = new EventEmitter<GroupedPayment>();
  @Output() onOpenHistory = new EventEmitter<GroupedPayment>();

  // Smart-Grid data as signal
  paymentData = signal<GroupedPayment[]>([]);

  // Column definitions
  columnDefs: ColDef<GroupedPayment>[] = [];

  // Grid configuration
  gridConfig: GridConfig<GroupedPayment> = {};

  // Track selected inner row (for expansion if needed in the future)
  selectedInner: GroupedPayment | null = null;

  constructor() {}

  ngOnInit() {
    this.initializeGrid();
    this.updateDataSource();
  }

  ngOnChanges() {
    this.updateDataSource();
  }

  /**
   * Initialize column definitions and grid configuration
   */
  private initializeGrid(): void {
    // Define columns
    this.columnDefs = [
      // 1. PAYMENT METHOD with ACTION BUTTONS
      {
        colId: 'paymentMethod',
        field: 'paymentType.name' as any,
        headerName: 'Tipo Pago',
        width: 250,
        minWidth: 200,
        sortable: false,
        filterable: false,
        cellRenderer: PaymentMethodActionsCellRendererComponent,
        cellRendererParams: {
          showActions: this.showActions,
          onOpenHistory: this.onOpenHistory,
          onOpenCreditNote: this.onOpenCreditNote,
        },
        cellClass: 'px-6 text-left',
        headerClass: 'px-6 text-left',
        footer: {
          staticValue: 'TOTAL',
          footerCellRenderer: 'text',
          footerClass: 'px-6 text-left font-bold uppercase',
        },
      },

      // 2. BANK
      {
        colId: 'bank',
        field: 'bank.name' as any,
        headerName: 'Banco',
        minWidth: 150,
        sortable: false,
        filterable: false,
        valueFormatter: (value) => value || '-',
        cellClass: 'px-6 text-left',
        headerClass: 'px-6 text-left',
        footer: {
          footerCellRenderer: 'blank',
        },
      },

      // 3. ACCOUNT NUMBER
      {
        colId: 'operationNumber',
        field: 'account.accountNumber' as any,
        headerName: 'Cuenta',
        minWidth: 120,
        maxWidth: 120,
        sortable: false,
        filterable: false,
        valueFormatter: (value) => value || '-',
        cellClass: 'px-6 text-center',
        headerClass: 'px-6 text-center',
        footer: {
          footerCellRenderer: 'blank',
        },
      },

      // 4. OVERPAYMENT
      {
        colId: 'overPayment',
        field: 'totalOverPayment' as any,
        headerName: `Sobrepago (${this.totalUnit})`,
        minWidth: 130,
        sortable: false,
        filterable: false,
        cellRenderer: DecimalCellRendererComponent,
        cellRendererParams: {
          decimals: 2,
          showCurrency: false
        },
        cellClass: 'px-6 text-right',
        headerClass: 'px-6 text-right',
        footer: {
          footerCellRenderer: PaymentFooterCellRendererComponent,
          footerCellRendererParams: {
            field: 'totalOverPayment',
            decimals: 2
          },
          footerClass: 'px-6 text-right font-bold',
        },
      },

      // 5. PAYING
      {
        colId: 'paying',
        field: 'totalPaying' as any,
        headerName: `Abono (${this.totalUnit})`,
        minWidth: 130,
        sortable: false,
        filterable: false,
        cellRenderer: DecimalCellRendererComponent,
        cellRendererParams: {
          decimals: 2,
          showCurrency: false
        },
        cellClass: 'px-6 text-right',
        headerClass: 'px-6 text-right',
        footer: {
          footerCellRenderer: PaymentFooterCellRendererComponent,
          footerCellRendererParams: {
            field: 'totalPaying',
            decimals: 2
          },
          footerClass: 'px-6 text-right font-bold',
        },
      },

      // 6. TOTAL (Computed)
      {
        colId: 'total',
        headerName: `Total (${this.totalUnit})`,
        minWidth: 130,
        sortable: false,
        filterable: false,
        valueGetter: (data: GroupedPayment) => (data.totalPaying ?? 0) + (data.totalOverPayment ?? 0),
        cellRenderer: DecimalCellRendererComponent,
        cellRendererParams: {
          decimals: 2,
          showCurrency: false
        },
        cellClass: 'px-6 text-right',
        headerClass: 'px-6 text-right',
        footer: {
          footerCellRenderer: PaymentFooterCellRendererComponent,
          footerCellRendererParams: {
            field: 'computed',
            decimals: 2
          },
          footerClass: 'px-6 text-right font-bold',
        },
      },
    ];

    // Configure grid
    this.gridConfig = {
      rowIdField: '_index' as any, // Use array index as row ID

      // Footer configuration
      footer: {
        enabled: true,
        sticky: true,
        height: '48px',
        footerClass: 'bg-gray-50 dark:bg-gray-800',
      },

      // Disable sorting and filtering
      sorting: {
        enabled: false,
      },
      columnFilters: {
        enabled: false,
      },

      // Disable pagination (show all rows)
      pagination: {
        enabled: false,
      },

      // Disable selection
      selection: {
        enabled: false,
      },

      // Styling
      styling: {
        density: 'comfortable',
        maxHeight: 'none',
        showBorders: true,
        stripedRows: false,
        rowHover: true,
        tableClass: 'min-w-500 mat-elevation-z',
      },

      // Performance
      performance: {
        virtualScrolling: false,
      },
    };
  }

  /**
   * Update data source with payments or rawPayments
   */
  private updateDataSource() {
    let finalPayments: GroupedPayment[] = [];

    if (this.payments && this.payments?.length > 0) {
      finalPayments = this.payments;
    } else if (this.rawPayments && this.rawPayments?.length > 0) {
      finalPayments = this.groupPayments(this.rawPayments);
    }

    // Add _index to each payment for rowIdField
    finalPayments = finalPayments.map((payment, index) => ({
      ...payment,
      _index: index,
    } as any));

    this.paymentData.set(finalPayments);
  }

  /**
   * Computed totals (for reference, not used in smart-grid)
   */
  get totalPaying(): number {
    return this.paymentData().reduce(
      (total, payment) => total + (payment.totalPaying || 0),
      0
    );
  }

  get totalOverPayment(): number {
    return this.paymentData().reduce(
      (total, payment) => total + (payment.totalOverPayment || 0),
      0
    );
  }

  get totalSum(): number {
    return this.totalPaying + this.totalOverPayment;
  }

  openCreditNoteDetail(payment: GroupedPayment) {
    this.onOpenCreditNote.emit(payment);
  }

  toggleInner(payment: GroupedPayment) {
    this.selectedInner = this.selectedInner?.id === payment.id ? null : payment;
    this.onToggleInner.emit(payment);
  }

  private groupPayments(
    rawPayments: OrderPaymentAttributes[]
  ): GroupedPayment[] {
    const grouped = new Map<string, GroupedPayment>();

    rawPayments.forEach((payment) => {
      let key = payment.paymentType?.code || "unknown";
      let groupKey = key;

      const hasBankDetails = payment.bank && payment.account;

      if (hasBankDetails) {
        groupKey = `${key}_${payment.bank.code}_${payment.account.accountNumber}`;
      }

      if (!grouped.has(groupKey)) {
        const newGroupPayment: GroupedPayment = {
          paymentType: {
            code: payment.paymentType?.code,
            name: payment.paymentType?.name,
          },
          account: payment?.account,
          bank: payment?.bank,
          totalOverPayment: 0,
          totalPaying: 0,
          originalPayments: [],
        };

        grouped.set(groupKey, newGroupPayment);
      }

      const groupedPayment = grouped.get(groupKey)!;
      groupedPayment.totalOverPayment += payment.overpayment || 0;
      groupedPayment.totalPaying += payment.paying || 0;
      groupedPayment.originalPayments.push(payment);
    });

    return Array.from(grouped.values());
  }
 
}
